import React, { Fragment, useRef, useState, useEffect } from 'react';
import classname from 'classnames';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useDrag, useDrop } from 'react-dnd';
import { ButtonWithIcon } from '@els/els-react--button';
import { Flyout } from '@els/els-react--flyout';
import AppStorage from '@Services/AppStorage';
import QueryletHeader from '../Querylet/QueryletHeader';
import QueryletSearchField from '../Querylet/QueryletSearchField';
import OperatorSelection from '../OperatorSelection/OperatorSelection';
import { addToBuilderAction, mergeNodeItemAction, onPropUnSelectAction, ungroupAction, updateOperatorAction } from '../QueryletActions';
import { focusDragButtonOrSidePanel, focusNextDragButtonOrSidePanel } from '../Shared/focusManagement';
import { findNodeSiblingsWithIndex } from '../Shared/queryTreeUtils';
import '../QueryContainer/QueryContainer.scss';
import { OPERATORS_CACHE_KEY } from '../QueryPanel/hooks/useFetchOperator';
import { BOUND_OPS_CACHE_KEY } from '../QueryPanel/hooks/useFetchBoundOps';

function clearKeyboardTargetHighlight() {
  const highlightedNode = document.querySelector('.ql__prop--keyboard-target');
  highlightedNode?.classList.remove('ql__prop--keyboard-target');
}

function setKeyboardTargetHighlight(targetUuid) {
  clearKeyboardTargetHighlight();
  const targetNode = document.querySelector(`[data-uuid="${targetUuid}"]`)?.closest('.ql__prop');
  targetNode?.classList.add('ql__prop--keyboard-target');
}

function NodeRenderer({ index, node, nodesList, boundOps, operators, itemIndex, type, onToggleQuerylet }) {
  const ref = useRef(null);
  const sectionRef = useRef(null);
  const groupTimerRef = useRef(null);
  const [operatorList, setOperatorList] = useState(AppStorage.instance.get(OPERATORS_CACHE_KEY) || []);
  const [boundOpsList, setBoundOpsList] = useState(AppStorage.instance.get(BOUND_OPS_CACHE_KEY) || {});
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    if (operators) {
      setOperatorList(operators);
    }
  }, [operators]);

  useEffect(() => {
    if (boundOps) {
      setBoundOpsList(boundOps);
    }
  }, [boundOps]);

  useEffect(
    () => () => {
      if (groupTimerRef.current) {
        clearTimeout(groupTimerRef.current);
      }
      clearKeyboardTargetHighlight();
    },
    []
  );

  const dispatch = useDispatch();

  const handleKeyboardGroup = (direction) => {
    const siblingContext = findNodeSiblingsWithIndex(nodesList, node.uuid);
    if (!siblingContext) return;

    const targetIndex = siblingContext.index + (direction === 'up' ? -1 : 1);
    if (targetIndex < 0 || targetIndex >= (siblingContext?.siblings?.length || 0)) return;

    const targetNode = siblingContext?.siblings?.[targetIndex];
    if (!targetNode) return;
    const nodeName = node.groupName || node.displayName || 'item';
    const targetName = targetNode.groupName || targetNode.displayName || 'item';
    setKeyboardTargetHighlight(targetNode.uuid);
    // Set announcement first and defer dispatch so the aria-live region
    // is committed to the DOM before this component unmounts due to the merge.
    setAnnouncement(`${nodeName} grouped with ${targetName}`);

    if (groupTimerRef.current) {
      clearTimeout(groupTimerRef.current);
    }

    groupTimerRef.current = setTimeout(() => {
      dispatch(mergeNodeItemAction({ key: node.uuid, obj: node }, nodesList, { key: targetNode.uuid }));
      focusDragButtonOrSidePanel(targetNode);

      clearKeyboardTargetHighlight();
      groupTimerRef.current = null;
    }, 150);
  };

  const [collectedDragProps, dragRef] = useDrag({
    type: 'query-item',
    item: () => ({ index, id: node.id, key: node.uuid, itemIndex, obj: node }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  function subItemAddtoBuilder(dragIndex, collectedProps) {
    if (dragIndex.type === 'DragNode') {
      const indexValue = dragIndex.node?.type === 'form' ? dragIndex.node.displayName : dragIndex.node.id;
      dispatch(
        addToBuilderAction(dragIndex.node, 'default', {
          dragId: indexValue,
          dropId: collectedProps.uuid,
          issubItem: true,
          isForm: dragIndex.node.type === 'form',
        })
      );
    } else {
      dispatch(
        mergeNodeItemAction(dragIndex, nodesList, {
          type: 'subItem',
          key: collectedProps.uuid,
        })
      );
    }
  }

  function nodeAddtoBuilder(dragIndex, collectedProps) {
    const isForm = dragIndex.node?.type === 'form';
    /** Drag directly in node from side nav */
    if (dragIndex.type === 'DragNode') {
      const indexValue = dragIndex.node.type === 'form' ? dragIndex.node.displayName : dragIndex.node.id;
      dispatch(
        addToBuilderAction(dragIndex.node, 'default', {
          dragId: indexValue,
          dropId: collectedProps.uuid,
          isForm,
        })
      );
    } else {
      dispatch(
        mergeNodeItemAction(dragIndex, nodesList, {
          type: 'subItem',
          key: collectedProps.uuid,
        })
      );
    }
  }

  const [collectedProps, dropRef] = useDrop({
    accept: ['query-item', 'DragNode'],
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      isOverShallow: monitor.isOver({ shallow: true }),
      current: ref.current,
      uuid: ref.current?.getAttribute('data-uuid'),
    }),
    drop: (dragIndex, monitor) => {
      if (
        monitor.didDrop() ||
        collectedProps.uuid === dragIndex.key ||
        (dragIndex?.obj?.isShell && dragIndex.obj?.items && dragIndex.obj.items.findIndex((item) => item.uuid === collectedProps.uuid) > -1)
      )
        return;
      if (type === 'subItem') {
        subItemAddtoBuilder(dragIndex, collectedProps);
      } else if (type === 'Node' && collectedProps.isOver) {
        nodeAddtoBuilder(dragIndex, collectedProps);
      }
    },
  });

  const dragHandleRef = dragRef(ref);
  const sectionDropRef = dropRef(sectionRef);

  const handleOperatorChange = (operatorS) => {
    // eslint-disable-next-line no-shadow
    const { operator, qualifier, node } = operatorS;
    dispatch(updateOperatorAction({ operator, node, qualifier }));
  };

  return (
    <Fragment key={`parent-${node.displayName}`}>
      <span aria-live="assertive" aria-atomic="true" className="sr-only">
        {announcement}
      </span>
      {index > 0 && type !== 'subItem' && (
        <OperatorSelection node={node} key={`node-${node.displayName}`} updateOperator={handleOperatorChange} operators={operatorList} />
      )}
      <div
        className={classname('ql__prop', {
          'ql__prop--grouped-queries': itemIndex !== undefined,
          'ql__prop--sub-item': nodesList.items && nodesList.items?.length > 0,
          'ql__prop--with-wrapper': node.id === 'wrapper' && node.type !== 'history',
          'ql__prop--with-many-fields': !node.canExist && node.childrenCount > 1,
          'ql__prop--with-drag': collectedProps.isOver,
          'u-els-margin-top-1x': node.trackingSearchType?.[0] === 'forms',
        })}>
        <section
          className={classname('ql__prop__container', {
            'ql__prop--drag': collectedDragProps.isDragging,
            'ql__prop__container--openquerylet': node.openQuerylet,
            'ql__prop__container--structure': node.type === 'structure' || node.type === 'advanced',
            'ql__prop__container--text': node.type === 'fact',
          })}
          ref={sectionDropRef}
          key={`parent-${index}-${node.uuid}`}>
          <div
            className={classname('ql__prop__container', {
              'u-els-width-1o1': node.id === 'wrapper',
            })}
            ref={dragHandleRef}
            key={`node-${index}-${node.uuid}`}
            tabIndex={-1}
            data-uuid={node.uuid}>
            <div role="application">
              <Flyout trigger="hover" flyout="Drag" placement="bottom" theme="simple">
                <ButtonWithIcon
                  data-e2e="drag-button"
                  id={`querybuilder-dragfield-${node.id}`}
                  draggable
                  className="cursor-move"
                  aria-label={`Group ${node.groupName || node.displayName || 'item'}. Press Arrow Up or Arrow Down to group with adjacent item.`}
                  onClick={(dragE) => dragE.preventDefault()}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      handleKeyboardGroup('up');
                    } else if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      handleKeyboardGroup('down');
                    }
                  }}
                  linkIconSize="xxs"
                  iconOnly
                  type="borderless"
                  sprite="Drag">
                  drag
                </ButtonWithIcon>
              </Flyout>
            </div>
            <QueryletHeader
              node={node}
              parent={nodesList}
              isDragging={collectedDragProps.isDragging}
              onToggleQuerylet={onToggleQuerylet}
              isNumeric={node.canExist}
              isGrouped={node.items?.length > 0}
            />
            {node.items?.length > 0 && (
              <div className="ql__prop__header--group--actions">
                {node.groupName && !node.isImmutable && (
                  <Flyout trigger="hover" flyout={<span aria-hidden="true">Ungroup fields</span>} placement="bottom" theme="simple">
                    <ButtonWithIcon
                      data-e2e="group-button"
                      iconRight
                      layout="alt"
                      key="Ungroup"
                      id="groupedQuery"
                      type="link"
                      sprite="Link"
                      aria-label="Ungroup fields"
                      onClick={() => {
                        const firstUngroupedNode = node.items?.[0];
                        dispatch(ungroupAction(nodesList, node.uuid));
                        focusDragButtonOrSidePanel(firstUngroupedNode);
                      }}>
                      Ungroup
                    </ButtonWithIcon>
                  </Flyout>
                )}
                <Flyout trigger="hover" flyout={<span aria-hidden="true">Delete group</span>} placement="bottom" theme="simple">
                  <ButtonWithIcon
                    data-e2e="delete-querylet"
                    iconRight
                    layout="alt"
                    className="cursor-pointer u-els-margin-right-1o2"
                    id="querybuilder-deletequerylet-button"
                    key="closeQuerylet"
                    type="link"
                    sprite="Trash"
                    aria-label="Delete group"
                    onClick={() => {
                      const updatedNodesList = dispatch(onPropUnSelectAction(node.uuid, nodesList));
                      focusNextDragButtonOrSidePanel(updatedNodesList, node.uuid);
                    }}>
                    Delete
                  </ButtonWithIcon>
                </Flyout>
              </div>
            )}
          </div>
          {(!node.canExist || node.openQuerylet) && <QueryletSearchField subItem={node} boundOps={boundOpsList} index={index} />}
          {((node.type === 'advanced' && node.id !== 'wrapper') ||
            (node.type === 'structure' && node.id !== 'wrapper') ||
            (node.type === 'structure' && node.id !== 'wrapper') ||
            node.type === 'history' ||
            (node.trackingSearchType === 'fields' && !node.canExist && node.children?.length > 0)) && (
            <Flyout trigger="hover" flyout={<span aria-hidden="true">Delete Querylet</span>} placement="bottom" theme="simple">
              <ButtonWithIcon
                data-e2e="delete-querylet-button"
                id="closeQuerylet"
                sprite="Trash"
                className="cursor-pointer"
                iconOnly
                type="link"
                aria-label="Delete Querylet"
                onClick={() => {
                  const updatedNodesList = dispatch(onPropUnSelectAction(node.uuid, nodesList));
                  focusNextDragButtonOrSidePanel(updatedNodesList, node.uuid);
                }}>
                Delete Querylet
              </ButtonWithIcon>
            </Flyout>
          )}
        </section>
        {node.items &&
          node.items?.length > 0 &&
          node.items.map((subItem, cIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={`subItem-${cIndex}-${node.uuid}`}>
              {cIndex > 0  && (
                <OperatorSelection node={subItem} updateOperator={handleOperatorChange} operators={operatorList} />
              )}
              <div className="ql__container">
                <NodeRenderer
                  index={index}
                  itemIndex={cIndex}
                  type="subItem"
                  boundOps={boundOpsList}
                  node={subItem}
                  nodesList={nodesList}
                  onToggleQuerylet={onToggleQuerylet}
                />
              </div>
            </Fragment>
          ))}
      </div>
    </Fragment>
  );
}

NodeRenderer.propTypes = {
  node: PropTypes.object,
  index: PropTypes.number,
  itemIndex: PropTypes.number,
  boundOps: PropTypes.object,
  type: PropTypes.string,
  nodesList: PropTypes.array,
  operators: PropTypes.array,
  onToggleQuerylet: PropTypes.func,
};

export default NodeRenderer;
